import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { getProfileFieldsConfig } from '../../../api/contacts/formValuesConfig'
import contacts from '../../../api/contacts/hooks'
import { ContactType } from '../../../api/globalTypes'
import { locationGeneralFieldsConfig } from '../../../api/locations/formValuesConfig'
import { propertyGeneralFieldsConfig } from '../../../api/properties/formValuesConfig'
import properties from '../../../api/properties/hooks'
import { ENTITY_TYPE } from '../../../common/constants'
import { useLocationValues } from '../../../hooks/useLocationValues'
import { selectContactProfilePropertyServiceFilter, selectContactProfilePropertyTypeFilter } from '../../../store/formFilters'
import { mapValuesToFormFields } from '../../../utils/formUtils/forms'

export const useContactProfileFormSelectOptions = (
    contactType: ContactType,
    propertyServiceId?: string | number,
    propertyTypeId?: string | number
) => {
    const { storePropertyServiceId, storePropertyTypeId } = useSelector((state) => ({
        storePropertyServiceId: selectContactProfilePropertyServiceFilter(state),
        storePropertyTypeId: selectContactProfilePropertyTypeFilter(state),
    }))

    // edit mode vs create mode
    const selectedPropertyServiceId = propertyServiceId || storePropertyServiceId
    const selectedPropertyTypeId = propertyTypeId || storePropertyTypeId

    const propertyFilters = useMemo(() => {
        if (!selectedPropertyServiceId || !selectedPropertyTypeId) return undefined

        return { propertyService: selectedPropertyServiceId, propertyType: selectedPropertyTypeId }
    }, [selectedPropertyServiceId, selectedPropertyTypeId])

    const contactProfileFieldsConfig = useMemo(() => getProfileFieldsConfig(contactType), [contactType])

    const personFormFields = ['profileService', 'profileStatus', 'financing', 'profileDuration', 'definition']
    const companyFormFields = ['profileService', 'profileStatus', 'financing', 'profileDuration', 'companyDefinition']
    const propertyStaticFields = ['propertyService', 'propertyType', 'floorPosition']
    const propertyDynamicFields = ['propertySubtype', 'descriptions', 'equipment', 'heating', 'space', 'floor', 'utility']

    const contactFormFields = contactType === ContactType.Person ? personFormFields : companyFormFields

    const { data: contactValues, isPending: isContactValuesPending } = contacts.FormValues.useQuery(contactFormFields)
    const { data: staticFormValues, isPending: isStaticFormValuesPending } = properties.FormValues.useQuery(propertyStaticFields)
    const { data: dynamicFormValues, isPending: isDynamicFormValuesPending } = properties.FormValues.useQuery(
        propertyDynamicFields,
        propertyFilters,
        {
            enabled: Boolean(selectedPropertyServiceId && selectedPropertyTypeId),
        }
    )

    const { locationValues, isPending: isLocationValuesPending } = useLocationValues(
        contactType === ContactType.Person ? ENTITY_TYPE.CONTACT : ENTITY_TYPE.COMPANY
    )

    const propertyValues = useMemo(
        () => ({
            ...staticFormValues,
            ...dynamicFormValues,
        }),
        [staticFormValues, dynamicFormValues]
    )

    const contactSelectOptions = useMemo(() => {
        const options = mapValuesToFormFields(contactValues, contactProfileFieldsConfig)

        if (options.contact_profile_service_id) {
            // ids for offer profile services that should be hidden in contact profile form
            const offerProfileServiceIds = ['1', '2']

            options.contact_profile_service_id = options.contact_profile_service_id.filter((opt) => !offerProfileServiceIds.includes(opt.value))
        }
        return options
    }, [contactValues, contactProfileFieldsConfig])

    const propertySelectOptions = useMemo(() => {
        return mapValuesToFormFields(propertyValues, propertyGeneralFieldsConfig)
    }, [propertyValues])

    const locationSelectOptions = useMemo(() => {
        return mapValuesToFormFields(locationValues, locationGeneralFieldsConfig)
    }, [locationValues])

    const selectOptions = useMemo(
        () => ({
            ...contactSelectOptions,
            ...propertySelectOptions,
            ...locationSelectOptions,
        }),
        [contactSelectOptions, propertySelectOptions, locationSelectOptions]
    )

    return {
        selectOptions,
        isPending:
            isContactValuesPending ||
            isStaticFormValuesPending ||
            (Boolean(selectedPropertyServiceId && selectedPropertyTypeId) && isDynamicFormValuesPending) ||
            isLocationValuesPending,
    }
}
